import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, BRAND } from "@/lib/api";
import PrintHeader from "@/components/PrintHeader";
import { ShieldCheck } from "lucide-react";

const STATUS_TONE = {
  planned: { bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-300" },
  confirmed: { bg: "bg-violet-100", text: "text-violet-800", border: "border-violet-300" },
  in_progress: { bg: "bg-amber-100", text: "text-amber-800", border: "border-amber-300" },
  completed: { bg: "bg-emerald-100", text: "text-emerald-800", border: "border-emerald-300" },
  cancelled: { bg: "bg-rose-100", text: "text-rose-800", border: "border-rose-300" },
};

function CheckRow({ checked, label }) {
  return (
    <div className="flex items-center gap-2 text-[11px]">
      <span className={`grid h-4 w-4 place-items-center rounded border ${checked ? "border-emerald-600 bg-emerald-600 text-white" : "border-border bg-white text-transparent"}`}>{checked ? "✓" : ""}</span>
      <span className={checked ? "text-ink" : "text-ink-muted"}>{label}</span>
    </div>
  );
}

function SignaturePanel({ role, name, signatureUrl }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="text-[9px] uppercase tracking-widest text-ink-muted">{role}</div>
      <div className="mt-1 mb-6 min-h-[44px] border-b border-dashed border-border">
        {signatureUrl && <img src={signatureUrl} alt={role} className="h-10 w-auto" />}
      </div>
      <div className="text-[11px] font-medium text-ink">{name || "—"}</div>
      <div className="text-[9px] uppercase tracking-wider text-ink-muted">Signature &amp; date</div>
    </div>
  );
}

export default function PrintOtNote() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const printedRef = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: r } = await api.get(`/ot/bookings/${id}`);
        setData(r);
        if (!printedRef.current) {
          printedRef.current = true;
          setTimeout(() => window.print(), 600);
        }
      } catch (e) { setData({ error: true }); }
    })();
  }, [id]);

  if (!data) return <div className="grid h-screen place-items-center text-sm text-ink-muted">Loading operative record…</div>;
  if (data.error) return <div className="grid h-screen place-items-center text-sm text-rose-600">Could not load this OT record.</div>;

  const { booking: b, note: n, patient: p, hospital: h, branch: br, surgeon: s } = data;
  const tone = STATUS_TONE[b.status] || STATUS_TONE.planned;

  const PRE_OP = [
    { key: "consent_signed", label: "Consent signed" },
    { key: "fitness_uploaded", label: "Fitness clearance reviewed" },
    { key: "investigations_reviewed", label: "Investigations reviewed" },
    { key: "site_marked", label: "Surgical site marked" },
    { key: "nbm_confirmed", label: "NBM confirmed" },
    { key: "allergy_checked", label: "Allergy checked" },
    { key: "iol_confirmed", label: "IOL model & power confirmed" },
  ];

  return (
    <div className="min-h-screen bg-white p-6 text-[12px] text-ink print:p-3">
      <style>{`@page { size: A4 portrait; margin: 10mm; } @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } .no-print { display: none !important; } }`}</style>

      {/* Header */}
      <PrintHeader
        hospital={h}
        branch={br}
        subtitle="Operative Record · NABH-compliant"
        right={<>
          <div className="text-[9px] uppercase tracking-widest text-ink-muted">Operative Record</div>
          <div className={`mt-1 inline-block rounded-full border-2 px-3 py-0.5 text-[10px] font-semibold uppercase ${tone.bg} ${tone.text} ${tone.border}`}>Status: {(b.status || "").replace("_", " ")}</div>
        </>}
      />

      {/* Patient block */}
      <div className="mt-3 grid grid-cols-4 gap-3 rounded-md border border-border bg-secondary/40 p-3 text-[11px]">
        <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">Patient</div><div className="font-semibold">{(p && p.name) || b.patient_name}</div></div>
        <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">UHID</div><div className="font-mono">{(p && p.uhid) || b.patient_uhid}</div></div>
        <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">Age / Sex</div><div>{(p && p.age) || "—"} {p && p.gender ? `· ${p.gender}` : ""}</div></div>
        <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">Date</div><div>{b.scheduled_date}{b.scheduled_time ? ` · ${b.scheduled_time}` : ""}</div></div>
        <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">Surgery</div><div className="font-semibold">{b.surgery_name}</div></div>
        <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">Eye</div><div className="font-semibold">{(n && n.eye_operated) || b.eye}</div></div>
        <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">OT room</div><div>{(n && n.ot_room) || "—"}</div></div>
        <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">Start → End</div><div>{(n && n.start_time) || "—"} → {(n && n.end_time) || "—"}</div></div>
        {p && p.mobile && <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">Mobile</div><div>{p.mobile}</div></div>}
        {p && p.blood_group && <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">Blood group</div><div>{p.blood_group}</div></div>}
        {p && p.abha_number && <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">ABHA</div><div className="font-mono text-[10px]">{p.abha_number}</div></div>}
      </div>

      {/* Operative team & timing */}
      <div className="mt-3 rounded-md border border-border p-3">
        <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-brand">Operative team &amp; timing</div>
        <div className="grid grid-cols-4 gap-3 text-[11px]">
          <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">OT room</div><div>{(n && n.ot_room) || "—"}</div></div>
          <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">Start time</div><div>{(n && n.start_time) || "—"}</div></div>
          <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">End time</div><div>{(n && n.end_time) || "—"}</div></div>
          <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">Duration</div><div>{(() => {
            if (!n || !n.start_time || !n.end_time) return "—";
            try {
              const [sh, sm] = n.start_time.split(":").map(Number);
              const [eh, em] = n.end_time.split(":").map(Number);
              let mins = (eh * 60 + em) - (sh * 60 + sm);
              if (mins < 0) mins += 24 * 60;
              const h = Math.floor(mins / 60); const m = mins % 60;
              return `${h ? `${h}h ` : ""}${m}m`;
            } catch (_) { return "—"; }
          })()}</div></div>
          <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">Operating surgeon</div><div className="font-medium">{(s && s.name) ? `Dr. ${s.name}` : "—"}{s && s.qualification ? `, ${s.qualification}` : ""}</div></div>
          <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">OT nurse</div><div className="font-medium">{(n && n.nurse_name) || "—"}</div></div>
          <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">OT technician</div><div className="font-medium">{(n && n.technician_name) || "—"}</div></div>
          <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">Anaesthetist</div><div className="font-medium">{(n && n.anesthetist_name) || "—"}</div></div>
        </div>
      </div>

      {/* Pre-op checklist + WHO safety */}
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div className="rounded-md border border-border p-3">
          <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-brand">Pre-op checklist (NABH)</div>
          <div className="grid grid-cols-1 gap-1">
            {PRE_OP.map((row) => <CheckRow key={row.key} label={row.label} checked={!!(n && n.pre_op_checklist && n.pre_op_checklist[row.key])} />)}
          </div>
        </div>
        <div className="rounded-md border border-border p-3">
          <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-brand">WHO Surgical Safety Checklist</div>
          <div className="grid grid-cols-1 gap-1">
            <CheckRow label="Sign-In (before induction)" checked={!!(n && n.who_safety && n.who_safety.sign_in)} />
            <CheckRow label="Time-Out (before incision)" checked={!!(n && n.who_safety && n.who_safety.time_out)} />
            <CheckRow label="Sign-Out (before patient leaves OT)" checked={!!(n && n.who_safety && n.who_safety.sign_out)} />
          </div>
        </div>
      </div>

      {/* Surgery details */}
      <div className="mt-3 rounded-md border border-border p-3">
        <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-brand">Surgery details</div>
        <div className="grid grid-cols-3 gap-3 text-[11px]">
          <div className="col-span-3"><b>Procedure performed:</b> {(n && n.procedure_performed) || "—"}</div>
          <div><b>Anaesthesia:</b> {(n && n.anesthesia_type) || "—"}</div>
          <div className="col-span-2"><b>Consumables:</b> {(n && n.consumables_used) || "—"}</div>
          <div className="col-span-3"><b>Intra-op notes:</b> {(n && n.intra_op_notes) || "—"}</div>
          <div className="col-span-3"><b>Post-op notes:</b> {(n && n.post_op_notes) || "—"}</div>
          <div className="col-span-3"><b>Post-op instructions:</b> {(n && n.post_op_instructions) || "—"}</div>
          {n && n.complications && <div className="col-span-3"><b>Complications:</b> <span className="text-rose-700">{n.complications}</span></div>}
        </div>
      </div>

      {/* IOL / Implant — always render; placeholders if empty */}
      <div className="mt-3 rounded-md border border-border p-3">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-[10px] font-bold uppercase tracking-widest text-brand">IOL / Implant details</div>
          {n && n.iol && n.iol.implant_name && <div className="text-[10px] text-ink-muted">{n.iol.implant_name}</div>}
        </div>
        <div className="grid grid-cols-5 gap-3 text-[11px]">
          <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">Model</div><div>{(n && n.iol && n.iol.model) || "—"}</div></div>
          <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">Power</div><div>{(n && n.iol && n.iol.power) || "—"}</div></div>
          <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">Batch</div><div className="font-mono">{(n && n.iol && n.iol.batch_no) || "—"}</div></div>
          <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">Lot</div><div className="font-mono">{(n && n.iol && n.iol.lot_no) || "—"}</div></div>
          <div><div className="text-[9px] uppercase tracking-wider text-ink-muted">Expiry</div><div>{(n && n.iol && n.iol.expiry) || "—"}</div></div>
        </div>
        {/* Sticker paste box — physical IOL barcode/lot sticker is affixed here in the printed record (NABH practice) */}
        <div className="mt-3 flex items-start gap-4">
          <div>
            <div className="text-[9px] uppercase tracking-wider text-ink-muted">Paste IOL / implant sticker</div>
            <div className="mt-1 grid h-24 w-60 place-items-center rounded-md border-2 border-dashed border-ink/40 bg-secondary/30 text-[9px] uppercase tracking-widest text-ink-subtle">
              Affix sticker here
            </div>
          </div>
        </div>
      </div>

      {/* Signatures — 4 panels */}
      <div className="mt-4">
        <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-brand">Operative team — signatures</div>
        <div className="grid grid-cols-4 gap-3">
          <SignaturePanel role="Operating surgeon" name={(s && s.name) ? `Dr. ${s.name}${s.qualification ? `, ${s.qualification}` : ""}` : (n && n.surgeon_name) || ""} signatureUrl={s && s.signature_url} />
          <SignaturePanel role="OT nurse" name={(n && n.nurse_name) || ""} />
          <SignaturePanel role="OT technician" name={(n && n.technician_name) || ""} />
          <SignaturePanel role="Anaesthetist" name={(n && n.anesthetist_name) || ""} />
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between border-t border-border pt-2 text-[9px] text-ink-muted">
        <div>Generated {new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} IST · Audit-logged · NABH-compliant</div>
        <button onClick={() => window.print()} className="no-print rounded-md bg-brand px-3 py-1 text-white">Print again</button>
      </div>
    </div>
  );
}
